import { useMemo, useState, type FormEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { Input } from "@/shared/components/Input";
import { CampoDataNascimento } from "@/shared/components/CampoDataNascimento";
import { CampoSexo } from "@/shared/components/CampoSexo";
import type { ContatoIgreja } from "@/core/utils/contatoIgreja";
import { AvisoConsentimento } from "@/modules/visitantes/components/AvisoConsentimento";
import { CampoSelect, CampoTexto } from "@/modules/visitantes/components/Campos";
import { EscolhaSimNao } from "@/modules/visitantes/components/EscolhaSimNao";
import { SobreAPessoa, type ErrosSobre } from "@/modules/visitantes/components/SobreAPessoa";
import type {
  DadosFamiliar,
  DadosSobre,
  DadosVisitante,
} from "@/modules/visitantes/services/visitantesService";
import {
  MAX_FAMILIARES,
  OPCOES_PARENTESCO,
  ROTULO_CATEGORIA,
  ROTULO_PARENTESCO,
  SOBRE_VAZIO,
  categoriaDaIdade,
  respostasEmComum,
  resumoRespostas,
  rotuloAnos,
} from "@/modules/visitantes/utils/familia";
import {
  ESTADOS_CIVIS,
  calcularIdade,
  formatarTelefone,
  hojeSaoPaulo,
  mascararTelefoneDigitando,
  primeiroNome,
  soDigitos,
  telefoneValido,
} from "@/modules/visitantes/utils/regras";

/** Linha da etapa 2. `chave` só serve para o React (a lista pode ganhar/perder itens). */
export interface LinhaFamiliar {
  chave: number;
  parentesco: DadosFamiliar["parentesco"] | "";
  nome: string;
  nascimento: string;
  /** "masculino" | "feminino"; "" = não informado. De qualquer idade. */
  sexo: string;
  /**
   * Familiar de um pendente salvo antes do campo existir (sem sexo): na edição o sexo dele é
   * opcional. Linha nova sempre exige.
   */
  sexoOpcional: boolean;
  telefone: string;
  /** Opcional; só de quem tem 12 anos ou mais. */
  estadoCivil: string;
  /** "Mesmas respostas de [1ª pessoa]?" — Sim copia as respostas em comum; Não abre as perguntas dele. */
  mesmas: boolean;
  /** Respostas próprias (valem só quando `mesmas` é falso e a pessoa tem 12 anos ou mais). */
  sobre: DadosSobre;
}

let proximaChave = 1;
export const novaLinhaFamiliar = (): LinhaFamiliar => ({
  chave: proximaChave++,
  parentesco: "",
  nome: "",
  nascimento: "",
  sexo: "",
  sexoOpcional: false,
  telefone: "",
  estadoCivil: "",
  mesmas: true,
  sobre: { ...SOBRE_VAZIO },
});

interface Props {
  /** Dados da etapa 1 (só leitura aqui; nada foi salvo ainda). */
  principal: DadosVisitante;
  contatoIgreja?: Partial<ContatoIgreja> | null;
  linhas: LinhaFamiliar[];
  onLinhas: (linhas: LinhaFamiliar[]) => void;
  enviando: boolean;
  erro?: string | null;
  onCriar: (membros: DadosFamiliar[], consentimento: boolean) => void;
  onVoltar: () => void;
  /** Visitante pendente (líder de outro ministério): o texto fala em "enviar", não em "criar". */
  modoInforme?: boolean;
  /** Edição do pendente: o consentimento já foi registrado na criação (não pergunta de novo). */
  consentimentoRegistrado?: boolean;
  /** Texto do botão final. Padrão: "Criar N visitantes". */
  rotuloEnviar?: (total: number) => string;
}

type ErrosLinha = ErrosSobre &
  Partial<Record<"parentesco" | "nome" | "nascimento" | "sexo" | "telefone", string>>;

/**
 * Etapa 2 do cadastro em família: quem veio junto. Tudo é criado de uma vez no fim
 * (uma confirmação de consentimento para todos). A pessoa da etapa 1 é o adulto de referência:
 * o parentesco de cada familiar é "o que ele é para ela". Criança só guarda nome e idade,
 * com o contato dos adultos do grupo como responsáveis (regra do banco). Quem tem 12 anos ou mais
 * responde às mesmas perguntas "Sobre a pessoa" da primeira pessoa: um Sim/Não resolve o comum
 * (mesma igreja, cidade e preferência de contato); o Não abre as perguntas dele (VIS-DEC-037).
 * Menor de 18 anos exige o telefone de um adulto da família (VIS-DEC-038).
 */
export function FormularioFamilia({
  principal,
  contatoIgreja,
  linhas,
  onLinhas,
  enviando,
  erro,
  onCriar,
  onVoltar,
  modoInforme = false,
  consentimentoRegistrado = false,
  rotuloEnviar,
}: Props) {
  const hoje = hojeSaoPaulo();
  const [erros, setErros] = useState<Record<number, ErrosLinha>>({});
  const [consentimento, setConsentimento] = useState(consentimentoRegistrado);
  const [erroResp, setErroResp] = useState<string | undefined>();
  const [erroConsentimento, setErroConsentimento] = useState<string | undefined>();

  const idadePrincipal = calcularIdade(principal.nascimento, hoje);
  const primeiro = primeiroNome(principal.nome);
  // Respostas que o "Sim" copia da primeira pessoa (o pedido de oração nunca vai junto).
  const emComum = respostasEmComum(principal);
  const resumoComum = resumoRespostas(emComum);

  const mudar = (
    chave: number,
    campo: "parentesco" | "nome" | "nascimento" | "sexo" | "telefone" | "estadoCivil",
    valor: string,
  ) => onLinhas(linhas.map((l) => (l.chave === chave ? { ...l, [campo]: valor } : l)));
  const mudarLinha = (chave: number, parcial: Partial<LinhaFamiliar>) =>
    onLinhas(linhas.map((l) => (l.chave === chave ? { ...l, ...parcial } : l)));

  const infos = useMemo(
    () =>
      linhas.map((l) => {
        const idade = calcularIdade(l.nascimento, hoje);
        return { chave: l.chave, idade, categoria: categoriaDaIdade(idade) };
      }),
    [linhas, hoje],
  );

  const adultos = [
    principal.nome.trim(),
    ...linhas
      .filter((l, i) => infos[i]?.categoria === "adulto" && l.nome.trim())
      .map((l) => l.nome.trim()),
  ];
  const temCrianca = infos.some((i) => i.categoria === "crianca");
  const temMenor = infos.some((i) => i.categoria === "crianca" || i.categoria === "adolescente");
  // Contato dos responsáveis: o telefone da primeira pessoa ou, sem ele, o do primeiro adulto que tiver.
  const telefoneResp =
    soDigitos(principal.telefone) ||
    linhas
      .map((l, i) => (infos[i]?.categoria === "adulto" ? soDigitos(l.telefone) : ""))
      .find((tel) => telefoneValido(tel)) ||
    "";

  function validar(): Record<number, ErrosLinha> {
    const todos: Record<number, ErrosLinha> = {};
    linhas.forEach((l, i) => {
      const e: ErrosLinha = {};
      if (!l.parentesco) e.parentesco = `Informe quem é para ${primeiro}.`;
      const nome = l.nome.trim();
      if (nome.length < 3 || nome.length > 100) e.nome = "Informe o nome (de 3 a 100 caracteres).";
      if (!l.nascimento) e.nascimento = "Informe a data de nascimento.";
      else if (l.nascimento > hoje || l.nascimento < "1900-01-01")
        e.nascimento = "Data de nascimento inválida.";
      else if (principal.dataVisita && l.nascimento > principal.dataVisita)
        e.nascimento = "Data de nascimento depois da visita.";
      if (!l.sexo && !l.sexoOpcional) e.sexo = "Informe o sexo.";
      const idade = infos[i]?.idade ?? null;
      if (l.telefone && (idade === null || idade >= 12) && !telefoneValido(l.telefone))
        e.telefone = "Telefone inválido. Use DDD e número.";
      if (idade !== null && idade >= 12 && !l.mesmas && l.sobre.preferenciaContato === "visita") {
        if (!l.sobre.endereco.trim()) e.endereco = "Informe rua e número.";
        if (!l.sobre.bairro.trim()) e.bairro = "Informe o bairro.";
        if (!l.sobre.cidade.trim()) e.cidade = "Informe a cidade.";
      }
      if (Object.keys(e).length > 0) todos[l.chave] = e;
    });
    return todos;
  }

  function enviar(ev: FormEvent) {
    ev.preventDefault();
    const e = validar();
    setErros(e);
    if (!consentimento) {
      setErroConsentimento("Confirme que os adultos foram avisados e concordaram.");
      return;
    }
    setErroConsentimento(undefined);
    if (temMenor && !telefoneResp) {
      setErroResp(
        "Para menor de 18 anos, informe o telefone de um adulto da família: na etapa 1 ou no cadastro de um adulto desta etapa.",
      );
      return;
    }
    setErroResp(undefined);
    if (Object.keys(e).length > 0) return;
    onCriar(
      linhas.map((l, i) => {
        const idade = infos[i]?.idade ?? 0;
        return {
          parentesco: l.parentesco as DadosFamiliar["parentesco"],
          nome: l.nome.trim(),
          nascimento: l.nascimento,
          sexo: l.sexo,
          // Telefone próprio e "Sobre a pessoa" só de quem tem 12 anos ou mais.
          telefone: idade >= 12 ? soDigitos(l.telefone) : "",
          estadoCivil: idade >= 12 ? l.estadoCivil : "",
          // O pedido de oração é sempre de cada um (nunca copiado).
          sobre:
            idade >= 12
              ? { ...(l.mesmas ? emComum : l.sobre), pedidoOracao: l.sobre.pedidoOracao }
              : undefined,
        };
      }),
      consentimento,
    );
  }

  const total = 1 + linhas.length;
  const textoBotao = rotuloEnviar ? rotuloEnviar(total) : "Cadastrar";
  const resumo = [
    `${primeiro} (${idadePrincipal !== null && idadePrincipal >= 18 ? "adulto" : "principal"})`,
    ...linhas.map((l, i) => {
      const cat = infos[i]?.categoria;
      const nome = l.nome.trim() ? primeiroNome(l.nome) : "familiar";
      return `${nome}${cat ? ` (${ROTULO_CATEGORIA[cat].toLowerCase()})` : ""}`;
    }),
  ].join(", ");

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-surface p-4">
        <p className="text-sm font-bold text-text">{principal.nome}</p>
        <p className="text-xs text-muted">
          Dados da etapa 1{idadePrincipal !== null && ` · Adulto (${rotuloAnos(idadePrincipal)})`}.
          Nada foi salvo ainda: tudo é {modoInforme ? "enviado" : "criado"} ao final desta etapa.
        </p>
      </div>

      {linhas.map((l, i) => {
        const info = infos[i];
        const e = erros[l.chave] ?? {};
        const cat = info?.categoria ?? null;
        return (
          <fieldset
            key={l.chave}
            className="flex flex-col gap-3 rounded-lg border border-border p-4"
          >
            <legend className="px-1 text-sm font-semibold text-text">
              {l.nome.trim() || `Familiar ${i + 1}`}
            </legend>
            {linhas.length > 1 && (
              <button
                type="button"
                onClick={() => onLinhas(linhas.filter((x) => x.chave !== l.chave))}
                className="inline-flex min-h-9 items-center gap-1.5 self-end text-xs font-semibold text-danger hover:underline"
              >
                <Trash2 size={14} aria-hidden="true" /> Remover
              </button>
            )}
            <CampoSelect
              label={`Quem é para ${primeiro}? *`}
              name={`parentesco-${l.chave}`}
              value={l.parentesco}
              onChange={(ev) => mudar(l.chave, "parentesco", ev.target.value)}
              erro={e.parentesco}
            >
              <option value="">Escolha</option>
              {OPCOES_PARENTESCO.map((p) => (
                <option key={p} value={p}>
                  {ROTULO_PARENTESCO[p]}
                </option>
              ))}
            </CampoSelect>
            <Input
              label="Nome completo *"
              name={`nome-${l.chave}`}
              maxLength={100}
              value={l.nome}
              onChange={(ev) => mudar(l.chave, "nome", ev.target.value)}
              erro={e.nome}
              autoComplete="off"
            />
            <CampoDataNascimento
              label="Data de nascimento *"
              name={`nascimento-${l.chave}`}
              max={hoje}
              value={l.nascimento}
              onChange={(iso) => mudar(l.chave, "nascimento", iso)}
              erro={e.nascimento}
            />
            <CampoSexo
              legenda={l.sexoOpcional ? "Sexo" : "Sexo *"}
              name={`sexo-${l.chave}`}
              valor={l.sexo}
              onChange={(v) => mudar(l.chave, "sexo", v)}
              erro={e.sexo}
            />
            {cat && info?.idade !== null && info?.idade !== undefined && (
              <p className="-mt-1 text-xs font-medium text-primary">
                {ROTULO_CATEGORIA[cat]} ({rotuloAnos(info.idade)})
              </p>
            )}
            {cat !== "crianca" && (
              <Input
                label="Telefone ou WhatsApp (opcional)"
                name={`telefone-${l.chave}`}
                type="tel"
                inputMode="numeric"
                placeholder="(11) 91234-5678"
                value={mascararTelefoneDigitando(l.telefone)}
                onChange={(ev) => mudar(l.chave, "telefone", soDigitos(ev.target.value))}
                erro={e.telefone}
              />
            )}
            {info?.idade !== null && info?.idade !== undefined && info.idade >= 12 && (
              <CampoSelect
                label="Estado civil"
                name={`estadoCivil-${l.chave}`}
                value={l.estadoCivil}
                onChange={(ev) => mudar(l.chave, "estadoCivil", ev.target.value)}
              >
                <option value="">Não informar</option>
                {ESTADOS_CIVIS.map((o) => (
                  <option key={o.chave} value={o.chave}>
                    {o.rotulo}
                  </option>
                ))}
              </CampoSelect>
            )}
            {info?.idade !== null && info?.idade !== undefined && info.idade >= 12 && (
              <div className="flex flex-col gap-3 border-t border-border pt-3">
                <p className="text-sm font-bold text-text">
                  Sobre {l.nome.trim() ? primeiroNome(l.nome) : "o familiar"}
                </p>
                <EscolhaSimNao
                  legenda={`Responde igual a ${primeiro}?`}
                  ajuda="Frequenta outra igreja, cidade e como prefere ser procurado(a)."
                  name={`mesmas-${l.chave}`}
                  valor={l.mesmas}
                  onChange={(v) => mudarLinha(l.chave, { mesmas: v })}
                  rotuloSim="Sim, igual"
                  rotuloNao="Não, é diferente"
                />
                {l.mesmas ? (
                  <p className="rounded-md border border-border px-3 py-2 text-xs text-muted">
                    {resumoComum
                      ? `Vai ficar igual a ${primeiro}: ${resumoComum}.`
                      : `${primeiro} não respondeu a essas perguntas, então nada será guardado.`}
                  </p>
                ) : (
                  <div className="flex flex-col gap-3 border-l-2 border-primary/40 pl-3">
                    <SobreAPessoa
                      valor={l.sobre}
                      onMudar={(parcial) =>
                        mudarLinha(l.chave, { sobre: { ...l.sobre, ...parcial } })
                      }
                      erros={e}
                      prefixo={`f${l.chave}-`}
                      comMoldura={false}
                      comOracao={false}
                    />
                  </div>
                )}
                <CampoTexto
                  label={`Pedido de oração de ${l.nome.trim() ? primeiroNome(l.nome) : "o familiar"} (opcional)`}
                  name={`oracao-${l.chave}`}
                  rows={2}
                  maxLength={500}
                  value={l.sobre.pedidoOracao}
                  onChange={(ev) =>
                    mudarLinha(l.chave, { sobre: { ...l.sobre, pedidoOracao: ev.target.value } })
                  }
                />
              </div>
            )}
            {cat === "adulto" && (
              <p className="text-xs text-muted">
                Adulto concorda por si mesmo: pergunte a ele também.
              </p>
            )}
            {(cat === "crianca" || cat === "adolescente") && (
              <p className="text-xs text-muted">
                {cat === "crianca" && "Criança: só nome e idade. "}
                Responsáveis: {adultos.join(" e ")}
                {telefoneResp ? ` · contato ${formatarTelefone(telefoneResp)}` : ""}.
              </p>
            )}
          </fieldset>
        );
      })}

      <button
        type="button"
        disabled={linhas.length >= MAX_FAMILIARES}
        onClick={() => onLinhas([...linhas, novaLinhaFamiliar()])}
        className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/50 px-4 text-sm font-semibold text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus size={16} aria-hidden="true" /> Adicionar familiar
      </button>
      {linhas.length >= MAX_FAMILIARES && (
        <p className="-mt-2 text-xs text-muted">
          Máximo de {MAX_FAMILIARES} familiares por cadastro.
        </p>
      )}

      {!consentimentoRegistrado && (
        <AvisoConsentimento
          variante="familia"
          contato={contatoIgreja}
          comCrianca={temCrianca}
          marcado={consentimento}
          onMarcar={(v) => {
            setConsentimento(v);
            if (v) setErroConsentimento(undefined);
          }}
          erro={erroConsentimento}
        />
      )}

      {temMenor && !telefoneResp && (
        <p role="alert" className="rounded-md bg-warning/10 px-4 py-3 text-sm text-warning">
          Há menor de 18 anos no grupo e nenhum adulto com telefone. Informe o telefone de um adulto
          (na etapa 1 ou aqui) para o ministério conseguir falar com o responsável.
        </p>
      )}
      {erroResp && (
        <p role="alert" className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erroResp}
        </p>
      )}

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erro}
        </p>
      )}

      <p className="text-xs text-muted">
        Serão {modoInforme ? "enviados para avaliação" : "criados"}: {resumo}.
      </p>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          onClick={onVoltar}
          disabled={enviando}
          className="min-h-11 bg-surface-2 text-text hover:bg-border"
        >
          Voltar
        </Button>
        <Button type="submit" carregando={enviando} disabled={!consentimento} className="min-h-11">
          {textoBotao}
        </Button>
      </div>
    </form>
  );
}
